/**
 * Ahead — where each balance is heading, from what is scheduled in YNAB.
 *
 * The screen YNAB does not have. It leads with the one sentence that matters — does
 * any account go below zero, and when — because a chart that makes you hunt for the
 * dip has buried the lede. The list under it is the forecast's working: every
 * scheduled movement with the balance straight after it, so a surprising line can be
 * traced to the row that caused it.
 */

import { useQuery } from '@tanstack/react-query'
import { type AccountForecastView, fetchForecast, queryKeys, type UpcomingRow } from '../api.ts'
import { BalanceLine } from '../components/BalanceLine.tsx'
import { fmt, shortDate } from '../lib/format.ts'
import { HORIZONS } from '../lib/router.ts'

export function Ahead({ days, onDays }: { days: number; onDays: (days: number) => void }) {
  const forecast = useQuery({
    queryKey: queryKeys.forecast(days),
    queryFn: () => fetchForecast(days),
  })
  const data = forecast.data

  const breaches = (data?.accounts ?? []).filter((a) => a.breach)

  return (
    <section className="flex flex-col gap-5 px-4 pt-[22px] pb-[60px] sm:px-7">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1.5">
          <h2 className="m-0 font-display text-[34px] font-normal leading-none">Ahead</h2>
          <p className="m-0 max-w-[600px] text-[14px] text-muted">
            Every account walked forward through what is scheduled in YNAB. Only scheduled
            transactions count — groceries and anything else unscheduled are not in the line, so if
            it looks rosy, the schedule is the thing to fill in.
          </p>
        </div>
        <div className="flex rounded-full bg-segment p-[3px]">
          {HORIZONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onDays(option)}
              className={`rounded-full px-[14px] py-[7px] text-[12.5px] ${
                days === option
                  ? 'bg-segment-active font-medium text-ink shadow-[var(--shadow-raise)]'
                  : 'bg-transparent text-muted hover:text-ink'
              }`}
            >
              {option === 365 ? 'A year' : `${option} days`}
            </button>
          ))}
        </div>
      </div>

      {forecast.isPending && <p className="text-[14px] text-muted">Walking the schedule…</p>}
      {forecast.isError && (
        <p className="rounded-card bg-blush-soft p-4 text-[14px] text-blush-deep">
          {forecast.error.message}
        </p>
      )}

      {data && (
        <>
          <div
            className={`rounded-card border px-5 py-4 ${
              breaches.length > 0 ? 'border-blush bg-blush-soft' : 'border-sky-line bg-sky-soft'
            }`}
          >
            {breaches.length === 0 ? (
              <p className="m-0 text-[15px] text-ink">
                Nothing scheduled takes an account below zero in the next {days} days.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {breaches.map((account) => (
                  <li key={account.id} className="text-[15px] text-blush-text">
                    <span className="font-medium">{account.name}</span> goes below zero on{' '}
                    {shortDate(account.breach?.date ?? '')}, and bottoms out at{' '}
                    {fmt(account.low.balance)} on {shortDate(account.low.date)}.
                  </li>
                ))}
              </ul>
            )}
          </div>

          {data.cash && (
            <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
                    Cash on hand
                  </span>
                  <span className="font-display text-[30px] leading-none">
                    {fmt(data.cash.start)}
                  </span>
                </div>
                <span className="text-[13px] text-muted">
                  {fmt(data.cash.end)} by {shortDate(data.through)}
                </span>
              </div>
              <BalanceLine
                points={data.cash.points}
                from={data.today}
                through={data.through}
                low={data.cash.low}
                label="Cash on hand"
              />
              <p className="m-0 text-[12px] text-muted">
                Everyday, savings and cash accounts together. Moving money between them does not
                move this line; paying a card does, because that is when the money leaves.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {data.accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                from={data.today}
                through={data.through}
              />
            ))}
          </div>

          <Upcoming rows={data.upcoming} />
        </>
      )}
    </section>
  )
}

function AccountCard({
  account,
  from,
  through,
}: {
  account: AccountForecastView
  from: string
  through: string
}) {
  return (
    <div className="flex flex-col gap-2 rounded-card border border-line bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[14px] font-medium">{account.name}</span>
        <span
          className={`text-[14px] ${account.hasFloor && account.start < 0 ? 'text-blush-deep' : 'text-ink'}`}
        >
          {fmt(account.start)}
        </span>
      </div>
      <BalanceLine
        points={account.points}
        from={from}
        through={through}
        low={account.low}
        height={56}
        compact
        label={account.name}
        hasFloor={account.hasFloor}
      />
      <div className="flex justify-between text-[11.5px] text-muted">
        <span>
          Low{' '}
          <span className={account.breach ? 'text-blush-deep' : 'text-ink-dim'}>
            {fmt(account.low.balance)}
          </span>{' '}
          · {shortDate(account.low.date)}
        </span>
        <span>
          then <span className="text-ink-dim">{fmt(account.end)}</span>
        </span>
      </div>
    </div>
  )
}

function Upcoming({ rows }: { rows: UpcomingRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-[14px] text-muted">
        Nothing is scheduled in YNAB for this stretch, so every line above is flat.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="m-0 font-display text-[22px] font-normal">What is scheduled</h3>
      <div className="overflow-x-auto rounded-card border border-line bg-card">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted">
              <th className="px-3 py-2 font-normal">Date</th>
              <th className="px-3 py-2 font-normal">Payee</th>
              <th className="hidden px-3 py-2 font-normal md:table-cell">Category</th>
              <th className="px-3 py-2 font-normal">Account</th>
              <th className="px-3 py-2 text-right font-normal">Amount</th>
              <th className="hidden px-3 py-2 text-right font-normal sm:table-cell">After</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                // A schedule lands once a day per account, its mirrored half on the other.
                key={`${row.scheduledId}-${row.accountId}-${row.date}`}
                className="border-t border-line-faint"
              >
                <td className="whitespace-nowrap px-3 py-2 text-ink-dim">{shortDate(row.date)}</td>
                <td className="px-3 py-2">
                  <span className="block max-w-[240px] truncate">{row.label}</span>
                  <span className="text-[11px] text-muted">{row.frequency}</span>
                </td>
                <td className="hidden px-3 py-2 text-ink-dim md:table-cell">
                  {row.categoryName ?? '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-ink-dim">{row.accountName}</td>
                <td
                  className={`whitespace-nowrap px-3 py-2 text-right ${
                    row.amount > 0 ? 'text-sky-deep' : 'text-ink'
                  }`}
                >
                  {fmt(row.amount)}
                </td>
                <td
                  className={`hidden whitespace-nowrap px-3 py-2 text-right sm:table-cell ${
                    row.balanceAfter < 0 ? 'text-blush-deep' : 'text-ink-dim'
                  }`}
                >
                  {fmt(row.balanceAfter)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
